import React, { useState } from 'react';
import { ShoppingCart, Plus, Minus, CheckCircle, Search, Users } from 'lucide-react';
import PharmacyOrderService from '../../services/api/PharmacyOrderService';
import { useToast } from '../Common/ToastProvider';

const BulkOrderingWizard = ({ onClose }) => {
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedResidents, setSelectedResidents] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [orderDetails, setOrderDetails] = useState({
    priority: 'Normal',
    deliveryDate: '',
    notes: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWard, setFilterWard] = useState('all');

  // Mock data
  const residents = [
    { id: 'RES001', name: 'Margaret Johnson', ward: 'Ward A', room: 'A101', medications: ['Paracetamol 500mg', 'Metformin 500mg'] },
    { id: 'RES002', name: 'Robert Smith', ward: 'Ward A', room: 'A102', medications: ['Aspirin 100mg', 'Warfarin 5mg'] },
    { id: 'RES003', name: 'Mary Williams', ward: 'Ward B', room: 'B201', medications: ['Insulin Glargine', 'Metformin 500mg'] },
    { id: 'RES004', name: 'John Brown', ward: 'Ward B', room: 'B202', medications: ['Paracetamol 500mg', 'Aspirin 100mg'] },
    { id: 'RES005', name: 'Elizabeth Davis', ward: 'Ward C', room: 'C301', medications: ['Morphine 10mg', 'Warfarin 5mg'] },
    { id: 'RES006', name: 'William Wilson', ward: 'Ward C', room: 'C302', medications: ['Insulin Glargine', 'Paracetamol 500mg'] }
  ];

  const medications = [
    { id: 'MED001', name: 'Paracetamol 500mg', unit: 'tablets', pbsCode: 'PBS-001', cost: 0.15, inStock: 150 },
    { id: 'MED002', name: 'Metformin 500mg', unit: 'tablets', pbsCode: 'PBS-002', cost: 0.25, inStock: 25 },
    { id: 'MED003', name: 'Aspirin 100mg', unit: 'tablets', pbsCode: 'PBS-003', cost: 0.10, inStock: 0 },
    { id: 'MED004', name: 'Warfarin 5mg', unit: 'tablets', pbsCode: 'PBS-004', cost: 0.35, inStock: 45 },
    { id: 'MED005', name: 'Insulin Glargine 100U/mL', unit: 'vials', pbsCode: 'PBS-005', cost: 25.00, inStock: 8 },
    { id: 'MED006', name: 'Morphine 10mg/mL', unit: 'vials', pbsCode: 'PBS-006', cost: 15.00, inStock: 12 }
  ];

  const filteredResidents = residents.filter(resident => {
    const matchesSearch = resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resident.room.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesWard = filterWard === 'all' || resident.ward === filterWard;
    return matchesSearch && matchesWard;
  });

  const handleResidentSelect = (resident) => {
    if (selectedResidents.find(r => r.id === resident.id)) {
      setSelectedResidents(selectedResidents.filter(r => r.id !== resident.id));
    } else {
      setSelectedResidents([...selectedResidents, resident]);
    }
  };

  const handleMedicationAdd = (medication, residentId) => {
    const existingItem = selectedMedications.find(item => 
      item.medicationId === medication.id && item.residentId === residentId
    );

    if (existingItem) {
      setSelectedMedications(selectedMedications.map(item =>
        item.medicationId === medication.id && item.residentId === residentId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setSelectedMedications([...selectedMedications, {
        medicationId: medication.id,
        medicationName: medication.name,
        residentId: residentId,
        quantity: 1,
        unit: medication.unit,
        cost: medication.cost
      }]);
    }
  };

  const handleQuantityChange = (medicationId, residentId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedMedications(selectedMedications.filter(item => 
        !(item.medicationId === medicationId && item.residentId === residentId)
      ));
    } else {
      setSelectedMedications(selectedMedications.map(item =>
        item.medicationId === medicationId && item.residentId === residentId
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const removeMedication = (medicationId, residentId) => {
    setSelectedMedications(selectedMedications.filter(item => 
      !(item.medicationId === medicationId && item.residentId === residentId)
    ));
  };

  const totalCost = selectedMedications.reduce((sum, item) => sum + (item.quantity * item.cost), 0);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmitOrder = async () => {
    try {
      for (const resident of selectedResidents) {
        const residentMeds = selectedMedications.filter((m) => m.residentId === resident.id);
        if (residentMeds.length === 0) continue;
        await PharmacyOrderService.create({
          residentId: resident.id,
          residentName: resident.name,
          ward: resident.ward,
          items: residentMeds.map((m) => ({
            drug: m.medication,
            qty: m.quantity || 1,
          })),
          notes: orderDetails.notes,
          priority: orderDetails.priority === 'Urgent' ? 'Urgent' : 'Medium',
          slaHours: orderDetails.priority === 'Urgent' ? 24 : 48,
        });
      }
      showToast(`Bulk order submitted for ${selectedResidents.length} resident(s)`, 'success');
    } catch (error) {
      console.error('Bulk order failed:', error);
      showToast('Error submitting bulk order', 'error');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-blue-600" />
            Bulk Ordering Wizard
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  currentStep >= step 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Select Residents */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Step 1: Select Residents</h3>
              <p className="text-gray-600">Choose residents for bulk medication ordering</p>
            </div>

            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search residents by name or room..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={filterWard}
                onChange={(e) => setFilterWard(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Wards</option>
                <option value="Ward A">Ward A</option>
                <option value="Ward B">Ward B</option>
                <option value="Ward C">Ward C</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredResidents.map((resident) => (
                <div
                  key={resident.id}
                  onClick={() => handleResidentSelect(resident)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedResidents.find(r => r.id === resident.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-800">{resident.name}</h4>
                      <p className="text-sm text-gray-600">{resident.ward} - {resident.room}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Current medications: {resident.medications.join(', ')}
                      </p>
                    </div>
                    {selectedResidents.find(r => r.id === resident.id) && (
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {selectedResidents.length} resident(s) selected
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Select Medications */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Step 2: Select Medications</h3>
              <p className="text-gray-600">Choose medications for each selected resident</p>
            </div>

            <div className="space-y-4">
              {selectedResidents.map((resident) => (
                <div key={resident.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {resident.name} ({resident.ward} - {resident.room})
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {medications.map((medication) => (
                      <div key={medication.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <h5 className="font-medium text-sm">{medication.name}</h5>
                          <p className="text-xs text-gray-600">
                            ${medication.cost.toFixed(2)} per {medication.unit} • Stock: {medication.inStock}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMedicationAdd(medication, resident.id)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <span className="text-sm font-medium min-w-[2rem] text-center">
                            {selectedMedications.find(item => 
                              item.medicationId === medication.id && item.residentId === resident.id
                            )?.quantity || 0}
                          </span>
                          <button
                            onClick={() => {
                              const existing = selectedMedications.find(item => 
                                item.medicationId === medication.id && item.residentId === resident.id
                              );
                              if (existing) {
                                handleQuantityChange(medication.id, resident.id, existing.quantity - 1);
                              }
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Order Details */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Step 3: Order Details</h3>
              <p className="text-gray-600">Review and finalize your bulk order</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-4">Order Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Residents:</span>
                    <span className="font-medium">{selectedResidents.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Medications:</span>
                    <span className="font-medium">{selectedMedications.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Cost:</span>
                    <span className="font-medium text-green-600">${totalCost.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-4">Order Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={orderDetails.priority}
                      onChange={(e) => setOrderDetails({...orderDetails, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Low">Low</option>
                      <option value="Normal">Normal</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Date</label>
                    <input
                      type="date"
                      value={orderDetails.deliveryDate}
                      onChange={(e) => setOrderDetails({...orderDetails, deliveryDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={orderDetails.notes}
                      onChange={(e) => setOrderDetails({...orderDetails, notes: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Additional notes for this order..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Selected Medications</h4>
              <div className="space-y-2">
                {selectedMedications.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span>{item.medicationName} for {selectedResidents.find(r => r.id === item.residentId)?.name}</span>
                    <span className="font-medium">{item.quantity} {item.unit} × ${item.cost.toFixed(2)} = ${(item.quantity * item.cost).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={currentStep === 1 && selectedResidents.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmitOrder}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Submit Order
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrderingWizard;
